import type { ReactElement } from 'react';
import { Lock, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import {
  useOidcCredentialStatus,
  type AuthProviderViewModel,
  type OidcCredentialViewModel,
} from '../api';
import { OidcCredentialStatusBadge } from './OidcCredentialStatusBadge';

export function AuthProvidersTable({
  rows,
  credentialsById,
  onEdit,
  onDelete,
  pendingDeleteId,
}: {
  readonly rows: readonly AuthProviderViewModel[];
  readonly credentialsById: ReadonlyMap<string, OidcCredentialViewModel>;
  readonly onEdit: (row: AuthProviderViewModel) => void;
  readonly onDelete: (row: AuthProviderViewModel) => void;
  readonly pendingDeleteId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.admin.authProviders.table';

  return (
    <DataTableTable aria-label={t('routes.admin.authProviders.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.host`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[9rem]">
            {t(`${col}.type`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.credential`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[7rem]">
            {t(`${col}.status`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[8rem]">
            {t(`${col}.serverStatus`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[6rem]">
            {t(`${col}.actions`)}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => (
          <ProviderRow
            key={row.id}
            row={row}
            credential={credentialsById.get(row.credentialId)}
            onEdit={onEdit}
            onDelete={onDelete}
            pendingDeleteId={pendingDeleteId}
          />
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}

function ProviderRow({
  row,
  credential,
  onEdit,
  onDelete,
  pendingDeleteId,
}: {
  readonly row: AuthProviderViewModel;
  readonly credential: OidcCredentialViewModel | undefined;
  readonly onEdit: (row: AuthProviderViewModel) => void;
  readonly onDelete: (row: AuthProviderViewModel) => void;
  readonly pendingDeleteId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.admin.authProviders.table';
  // PTV2-1383: the provider inherits its type from the bound credential; show the credential's type
  // (falling back to the server-derived value on the provider).
  const inheritedType = credential?.type ?? row.type;
  // The synthetic built-in provider (read-only) is greyed when its bootstrap-IdP probe is down.
  const statusQuery = useOidcCredentialStatus(row.credentialId);
  const dimmed = row.readOnly && (statusQuery.data?.ok === false || statusQuery.isError);

  return (
    <DataTableTr className={`even:bg-muted/55${dimmed ? ' opacity-50' : ''}`}>
      <DataTableTd className="h-[40px] py-0 font-mono text-xs text-foreground/90" dataOffset>
        <span className="inline-flex items-center gap-2">
          {row.host}
          {row.readOnly && (
            <Badge variant="outline" className="font-sans">
              {t(`${col}.builtInBadge`)}
            </Badge>
          )}
        </span>
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        {inheritedType !== null ? (
          <Badge variant="outline">{t(`routes.admin.authProviders.typeLabel.${inheritedType}`)}</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0 text-muted-foreground" dataOffset={false}>
        {credential?.label ?? t(`${col}.credentialUnknown`)}
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        <Badge variant={row.enabled ? 'default' : 'outline'}>
          {row.enabled ? t(`${col}.enabledBadge`) : t(`${col}.disabledBadge`)}
        </Badge>
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        {row.credentialId.length > 0 ? (
          <OidcCredentialStatusBadge credentialId={row.credentialId} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        {row.readOnly ? (
          <span
            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            title={t(`${col}.builtInHint`)}
          >
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            {t(`${col}.builtInBadge`)}
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label={t(`${col}.editAction`)}
              title={t(`${col}.editAction`)}
              onClick={() => {
                onEdit(row);
              }}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={t(`${col}.deleteAction`)}
              title={t(`${col}.deleteAction`)}
              loading={pendingDeleteId === row.id}
              onClick={() => {
                onDelete(row);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      </DataTableTd>
    </DataTableTr>
  );
}
